import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "./config/API";

function TenantsAndGuests() {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [filterType, setFilterType] = useState("ALL");
  const [mobileSearch, setMobileSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const loggedUser = JSON.parse(localStorage.getItem("user"));
  const emailId = loggedUser?.emailId || "";

  useEffect(() => {
    if (emailId) {
      fetchData();
    }
  }, [emailId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const user = JSON.parse(localStorage.getItem("user"));

      if (!user || !user.jwtToken) {
        alert("Session Expired. Please login again.");
        navigate("/");
        return;
      }

      const res = await fetch(
        `${API.V1}/user/getTenantsAndGuestsData?emailId=${encodeURIComponent(
          emailId,
        )}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.jwtToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (res.status === 401) {
        localStorage.clear();
        alert("Session Expired. Please login again.");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      const result = await res.json();

      if (result.statusCode === 200) {
        setData(result.data || []);
      } else {
        setData([]);
        alert(result.message || "No Data Found");
      }
    } catch (err) {
      console.error(err);
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let temp = [...data];

    if (filterType === "TENANT") {
      temp = temp.filter((i) => i.userStayingType?.toLowerCase() === "tenant");
    }

    if (filterType === "GUEST") {
      temp = temp.filter((i) => i.userStayingType?.toLowerCase() === "guest");
    }

    if (mobileSearch.trim()) {
      temp = temp.filter((i) =>
        i.contactNo?.toString().includes(mobileSearch.trim()),
      );
    }

    return temp;
  }, [data, filterType, mobileSearch]);

  const tenantCount = data.filter(
    (i) => i.userStayingType?.toLowerCase() === "tenant",
  ).length;

  const guestCount = data.filter(
    (i) => i.userStayingType?.toLowerCase() === "guest",
  ).length;

  const totalCount = data.length;

  const handleView = (row) => {
    navigate(`/userDetails/${row.userRegisterId}`);
  };

  return (
    <div style={styles.page}>
      <div style={styles.mainContainer}>
        {/* ================= TITLE ================= */}
        <div style={styles.titleSection}>
          <h1 style={styles.title}>🏠 Tenants & Guests</h1>
          <p style={styles.subtitle}>
            Manage tenant and guest booking information
          </p>
        </div>

        {/* ================= FILTER BAR ================= */}
        <div style={styles.topBar}>
          <div style={styles.btnGroup}>
            <button
              style={filterType === "ALL" ? styles.activeBtn : styles.btn}
              onClick={() => setFilterType("ALL")}
            >
              All ({totalCount})
            </button>

            <button
              style={
                filterType === "TENANT" ? styles.activeBtn : styles.btnBlue
              }
              onClick={() => setFilterType("TENANT")}
            >
              Tenants ({tenantCount})
            </button>

            <button
              style={
                filterType === "GUEST" ? styles.activeBtn : styles.btnGreen
              }
              onClick={() => setFilterType("GUEST")}
            >
              Guests ({guestCount})
            </button>
          </div>

          <input
            type="text"
            placeholder="Search Mobile Number"
            value={mobileSearch}
            onChange={(e) => setMobileSearch(e.target.value)}
            style={styles.searchBox}
          />
        </div>

        {/* ================= TABLE ================= */}
        <div style={styles.tableContainer}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : filteredData.length === 0 ? (
            <div style={styles.noData}>
              <div style={styles.noDataIcon}>👤</div>
              <h3>No Users Found</h3>
              <p>There are no records matching your filter.</p>
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Mobile</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Booking Mode</th>
                    <th style={styles.th}>Bed</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Bed Status</th>
                    <th style={styles.th}>Payment</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredData.map((row, index) => (
                    <tr key={row.userRegisterId || index} style={styles.tr}>
                      {/* NAME */}
                      <td style={styles.td}>
                        <div style={styles.userName}>{row.fullName || "-"}</div>
                        <div style={styles.registerId}>
                          ID: {row.userRegisterId || "-"}
                        </div>
                      </td>

                      {/* MOBILE */}
                      <td style={styles.td}>{row.contactNo || "-"}</td>

                      {/* EMAIL */}
                      <td style={styles.td}>
                        <span style={styles.email}>{row.emailId || "-"}</span>
                      </td>

                      {/* BOOKING MODE */}
                      <td style={styles.td}>
                        <span style={styles.bookingBadge}>
                          {row.bookingMode || "-"}
                        </span>
                      </td>

                      {/* BED */}
                      <td style={styles.td}>
                        <span style={styles.bedBadge}>
                          {row.bedNumber || "-"}
                        </span>
                      </td>

                      {/* TYPE */}
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.badge,
                            background:
                              row.userStayingType?.toLowerCase() === "tenant"
                                ? "#2563eb"
                                : "#16a34a",
                          }}
                        >
                          {row.userStayingType || "-"}
                        </span>
                      </td>

                      {/* BED STATUS */}
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            background:
                              row.bedStatus === "BOOKED"
                                ? "#dcfce7"
                                : "#fef3c7",
                            color:
                              row.bedStatus === "BOOKED"
                                ? "#15803d"
                                : "#b45309",
                          }}
                        >
                          {row.bedStatus || "-"}
                        </span>
                      </td>

                      {/* PAYMENT */}
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            background:
                              row.paymentStatus === "PAID"
                                ? "#dcfce7"
                                : "#fee2e2",
                            color:
                              row.paymentStatus === "PAID"
                                ? "#15803d"
                                : "#dc2626",
                          }}
                        >
                          {row.paymentStatus || "PENDING"}
                        </span>
                      </td>

                      {/* ACTION */}
                      <td style={styles.td}>
                        <button
                          style={styles.viewBtn}
                          onClick={() => handleView(row)}
                        >
                          👁 View & Update
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TenantsAndGuests;

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    padding: "20px",
    background: "#f1f5f9",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    boxSizing: "border-box",
  },

  mainContainer: {
    width: "100%",
    maxWidth: "1800px",
    margin: "0 auto",
  },

  titleSection: {
    textAlign: "center",
    marginBottom: "22px",
  },

  title: {
    margin: 0,
    fontSize: "clamp(24px, 3vw, 36px)",
    fontWeight: "800",
    color: "#0f172a",
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "14px",
  },

  topBar: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "15px",
  },

  btnGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },

  btn: {
    padding: "9px 15px",
    border: "none",
    borderRadius: "8px",
    background: "#64748b",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
  },

  btnBlue: {
    padding: "9px 15px",
    border: "none",
    borderRadius: "8px",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
  },

  btnGreen: {
    padding: "9px 15px",
    border: "none",
    borderRadius: "8px",
    background: "#10b981",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
  },

  activeBtn: {
    padding: "9px 15px",
    border: "none",
    borderRadius: "8px",
    background: "#111827",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
  },

  searchBox: {
    width: "250px",
    maxWidth: "100%",
    padding: "9px 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: "13px",
    boxSizing: "border-box",
    background: "#fff",
  },

  tableContainer: {
    width: "100%",
    background: "#fff",
    borderRadius: "14px",
    padding: "12px",
    boxSizing: "border-box",
    boxShadow: "0 4px 15px rgba(15, 23, 42, 0.06)",
  },

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
  },

  table: {
    width: "100%",
    minWidth: "1050px",
    borderCollapse: "collapse",
    tableLayout: "auto",
  },

  th: {
    padding: "12px 10px",
    background: "#f8fafc",
    color: "#334155",
    fontSize: "12px",
    fontWeight: "800",
    textAlign: "left",
    borderBottom: "2px solid #e2e8f0",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "11px 10px",
    color: "#334155",
    fontSize: "13px",
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },

  tr: {
    transition: "background 0.2s ease",
  },

  userName: {
    color: "#0f172a",
    fontWeight: "700",
    fontSize: "13px",
  },

  registerId: {
    color: "#64748b",
    fontSize: "11px",
    marginTop: "2px",
  },

  email: {
    color: "#475569",
    fontSize: "12px",
  },

  bookingBadge: {
    display: "inline-block",
    padding: "5px 8px",
    borderRadius: "6px",
    background: "#f1f5f9",
    color: "#475569",
    fontSize: "11px",
    fontWeight: "700",
  },

  bedBadge: {
    display: "inline-block",
    padding: "5px 9px",
    borderRadius: "6px",
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: "700",
  },

  badge: {
    display: "inline-block",
    padding: "5px 9px",
    borderRadius: "14px",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "700",
  },

  statusBadge: {
    display: "inline-block",
    padding: "5px 9px",
    borderRadius: "14px",
    fontSize: "11px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  viewBtn: {
    padding: "7px 11px",
    border: "none",
    borderRadius: "7px",
    background: "#06b6d4",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "12px",
    whiteSpace: "nowrap",
  },

  loading: {
    padding: "60px 20px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "15px",
  },

  noData: {
    padding: "60px 20px",
    textAlign: "center",
    color: "#64748b",
  },

  noDataIcon: {
    fontSize: "42px",
    marginBottom: "8px",
  },
};
